import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { criarAlimento, listarAlimentos } from "../db/queries";
import {
  GRUPOS_ORDEM,
  GRUPO_LABEL,
  REFEICAO_LABEL,
  type Alimento,
  type GrupoAlimentar,
  type Refeicao,
} from "../types";
import { POPULARES_POR_GRUPO, normalizarNome } from "../lib/catalogo";
import { refIlustracaoPorNome } from "../lib/catalogoImagens";
import { emojiAlimento } from "../lib/emoji";

/**
 * Preparar refeição (lado do cuidador): escolhe o tipo de refeição e monta o prato
 * por categoria (proteína, fibra, fruta…). Cadastrados e populares aparecem como o
 * MESMO chip selecionável — sem mistura de estilos. Tocar um popular o cria na
 * biblioteca (em teste) nos bastidores, mas visualmente nada muda. Em seguida abre o
 * modo escolha mostrando só esses essenciais. A oferta resultante fica marcada com a
 * refeição.
 */
const REFEICOES: Refeicao[] = ["cafe", "almoco", "lanche", "jantar"];
const MAX_POR_GRUPO = 3; // até 3 opções por categoria (proteína, carbo, fibra…)
const SEM_GRUPO = "_sem"; // bucket dos alimentos sem grupo definido

export function PrepararRefeicao() {
  const nav = useNavigate();
  const alimentos = useLiveQuery(() => listarAlimentos()) ?? [];
  const [refeicao, setRefeicao] = useState<Refeicao | null>(null);
  const [selecionados, setSelecionados] = useState<string[]>([]);

  // disponíveis pra montar o prato: tudo que não foi arquivado nem recusado
  // (inclui "a experimentar" — o catálogo padrão vem assim). Status mostra o badge.
  // Começa sem nada selecionado — o cuidador escolhe (sem pré-seleção automática).
  const disponiveis = alimentos.filter((a) => !a.arquivado && a.status !== "recusado");
  const semGrupo = disponiveis.filter((a) => !a.grupo);

  // nomes já na biblioteca (qualquer status) — pra não duplicar populares
  const naBiblioteca = new Set(alimentos.map((a) => normalizarNome(a.nome)));

  // grupo de um id selecionado + quantos já há naquele grupo (limite por categoria)
  const grupoDe = (id: string) => alimentos.find((a) => a.id === id)?.grupo ?? SEM_GRUPO;
  const contagemGrupo = (g: string) =>
    selecionados.filter((id) => grupoDe(id) === g).length;

  function toggle(id: string) {
    setSelecionados((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      const g = grupoDe(id);
      const n = s.filter((x) => grupoDe(x) === g).length;
      return n >= MAX_POR_GRUPO ? s : [...s, id];
    });
  }

  /** Toca um popular: cria na biblioteca (em teste) se preciso e seleciona. */
  async function tocarPopular(nome: string, grupo: GrupoAlimentar) {
    if (contagemGrupo(grupo) >= MAX_POR_GRUPO) return;
    const ref = refIlustracaoPorNome(nome); // ilustração padrão, se houver
    const id = await criarAlimento({
      nome,
      grupo,
      categoria: GRUPO_LABEL[grupo],
      status: "em_teste",
      fotos: ref ? [ref] : [],
    });
    setSelecionados((s) => (s.includes(id) ? s : [...s, id]));
  }

  function iniciar() {
    if (!refeicao || selecionados.length < 2) return;
    nav("/crianca", { state: { ids: selecionados, refeicao } });
  }

  // chip de um alimento já cadastrado (selecionável)
  const ChipCadastrado = (a: Alimento) => {
    const sel = selecionados.includes(a.id);
    const bloqueado = !sel && contagemGrupo(a.grupo ?? SEM_GRUPO) >= MAX_POR_GRUPO;
    return (
      <button
        key={a.id}
        className={`food-chip${sel ? " sel" : ""}`}
        disabled={bloqueado}
        onClick={() => toggle(a.id)}
      >
        <span className="fc-emoji" aria-hidden>
          {emojiAlimento(a.nome, a.categoria)}
        </span>
        <span className="fc-nome">{a.nome}</span>
        <span className="fc-marca" aria-hidden>
          {sel ? "✓" : "+"}
        </span>
      </button>
    );
  };

  // chip de um popular ainda não cadastrado (mesmo visual; cria + seleciona ao tocar)
  const ChipPopular = (nome: string, grupo: GrupoAlimentar) => {
    const cheio = contagemGrupo(grupo) >= MAX_POR_GRUPO;
    return (
      <button
        key={`pop-${grupo}-${nome}`}
        className="food-chip"
        disabled={cheio}
        onClick={() => tocarPopular(nome, grupo)}
      >
        <span className="fc-emoji" aria-hidden>
          {emojiAlimento(nome, grupo)}
        </span>
        <span className="fc-nome">{nome}</span>
        <span className="fc-marca" aria-hidden>
          +
        </span>
      </button>
    );
  };

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">preparar refeição</h1>

      {/* passo 1 — tipo de refeição */}
      <h2 className="secao-titulo">qual refeição?</h2>
      <div className="opcoes-refeicao">
        {REFEICOES.map((r) => (
          <button
            key={r}
            type="button"
            className={`opt-refeicao${refeicao === r ? " sel" : ""}`}
            aria-pressed={refeicao === r}
            onClick={() => setRefeicao(r)}
          >
            {REFEICAO_LABEL[r]}
          </button>
        ))}
      </div>

      {/* passo 2 — montar o prato (chips uniformes por categoria) */}
      {refeicao && (
        <>
          <p className="tela-sub">
            toque os alimentos pra montar o prato (até {MAX_POR_GRUPO} por categoria).
            a criança vai escolher entre os itens que você selecionar.
          </p>

          {GRUPOS_ORDEM.map((g) => {
            const itens = disponiveis.filter((a) => a.grupo === g);
            const populares = POPULARES_POR_GRUPO[g].filter(
              (nome) => !naBiblioteca.has(normalizarNome(nome)),
            );
            if (itens.length === 0 && populares.length === 0) return null;
            return (
              <section className="secao" key={g}>
                <h2 className="secao-titulo">{GRUPO_LABEL[g]}</h2>
                <div className="food-grid">
                  {itens.map(ChipCadastrado)}
                  {populares.map((nome) => ChipPopular(nome, g))}
                </div>
              </section>
            );
          })}
          {semGrupo.length > 0 && (
            <section className="secao">
              <h2 className="secao-titulo">sem grupo definido</h2>
              <div className="food-grid">{semGrupo.map(ChipCadastrado)}</div>
            </section>
          )}
        </>
      )}

      {/* barra fixa de ação */}
      {refeicao && (
        <button
          className="btn btn-primario btn-bloco btn-grande"
          disabled={selecionados.length < 2}
          onClick={iniciar}
        >
          {selecionados.length < 2
            ? "escolha ao menos 2 opções"
            : `iniciar escolha (${selecionados.length}) →`}
        </button>
      )}
    </main>
  );
}
